import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUp, ChevronDown, LayoutGrid, ChefHat, ArrowDownToLine, Bed, Bath, Shirt, BedDouble, Sofa, Maximize2, X } from 'lucide-react';
import { useTheme } from '../styles/ThemeContext';
interface NavigationRoomsPanelProps {
  showNavigation: boolean;
  showRooms: boolean;
  onToggleNavigation: () => void;
  onToggleRooms: () => void;
  onSelectRoom: (room: string) => void;
  onStartGuidedTour?: () => void;
  isGuidedTourActive?: boolean;
  className?: string;
  hideTabBar?: boolean;
}
const rooms = [{
  id: 'kitchen',
  name: 'Kitchen',
  icon: ChefHat
}, {
  id: 'basement',
  name: 'Basement',
  icon: ArrowDownToLine
}, {
  id: 'primary-bedroom',
  name: 'Primary Bedroom',
  icon: Bed
}, {
  id: 'ensuite',
  name: 'Ensuite',
  icon: Bath
}, {
  id: 'laundry',
  name: 'Laundry',
  icon: Shirt
}, {
  id: 'bedroom-2',
  name: 'Bedroom 2',
  icon: BedDouble
}, {
  id: 'bedroom-3',
  name: 'Bedroom 3',
  icon: BedDouble
}, {
  id: 'living-room',
  name: 'Living Room',
  icon: Sofa
}];
export function NavigationRoomsPanel({
  showNavigation,
  showRooms,
  onToggleNavigation,
  onToggleRooms,
  onSelectRoom,
  onStartGuidedTour,
  isGuidedTourActive = false,
  className = '',
  hideTabBar = false
}: NavigationRoomsPanelProps) {
  const {
    isDark
  } = useTheme();
  const [showLargeMap, setShowLargeMap] = useState(false);
  const [activeFloor, setActiveFloor] = useState<'Basement' | 'Main Floor' | 'Upper Floor'>('Main Floor');
  const floorImages = {
    Basement: 'https://cdn.magicpatterns.com/uploads/tUKozvi2HUbQqyNUdhAyLu/Zight_2026-02-06_at_11.09.38_AM.jpg',
    'Main Floor': 'https://cdn.magicpatterns.com/uploads/tUKozvi2HUbQqyNUdhAyLu/Zight_2026-02-06_at_11.09.38_AM.jpg',
    'Upper Floor': 'https://cdn.magicpatterns.com/uploads/tUKozvi2HUbQqyNUdhAyLu/Zight_2026-02-06_at_11.09.38_AM.jpg' // Placeholder
  };
  return <>
      {/* Large Map Modal */}
      <AnimatePresence data-id="element-1428">
        {showLargeMap && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-8" onClick={() => setShowLargeMap(false)} data-id="element-1429">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} className="relative max-w-4xl w-full aspect-[4/3] rounded-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-1430">
              {/* Floor Tabs */}
              <div className="absolute top-0 left-0 right-0 z-10 flex justify-center pt-6 pointer-events-none" data-id="element-1431">
                <div className="flex bg-black/50 backdrop-blur-md rounded-full p-1 pointer-events-auto border border-white/10" data-id="element-1432">
                  {(['Basement', 'Main Floor', 'Upper Floor'] as const).map(floor => <button key={floor} onClick={() => setActiveFloor(floor)} className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${activeFloor === floor ? 'bg-white text-black shadow-lg' : 'text-white/70 hover:text-white hover:bg-white/10'}`} data-id="element-1433">
                        {floor}
                      </button>)}
                </div>
              </div>

              {/* Close Button */}
              <button onClick={() => setShowLargeMap(false)} className="absolute top-6 right-6 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors border border-white/10" data-id="element-1434">
                <X className="w-6 h-6" data-id="element-1435" />
              </button>

              {/* Map Image */}
              <div className="w-full h-full relative" data-id="element-1436">
                <motion.img key={activeFloor} src={floorImages[activeFloor]} alt={`${activeFloor} Plan`} initial={{
              opacity: 0,
              scale: 1.05
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.3
            }} className="w-full h-full object-contain" data-id="element-1437" />

                {/* Current Position Dot (Only show on Main Floor for demo) */}
                {activeFloor === 'Main Floor' && <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" data-id="element-1438">
                    <span className="relative flex h-6 w-6" data-id="element-1439">
                      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${isDark ? 'bg-cyan-400' : 'bg-teal-500'}`} data-id="element-1440" />
                      <span className={`relative inline-flex rounded-full h-6 w-6 border-2 border-white ${isDark ? 'bg-cyan-500' : 'bg-teal-600'}`} data-id="element-1441" />
                    </span>
                    <div className={`absolute top-1/2 left-1/2 w-32 h-32 -translate-x-1/2 -translate-y-full origin-bottom bg-gradient-to-t ${isDark ? 'from-cyan-500/20' : 'from-teal-500/20'} to-transparent`} style={{
                clipPath: 'polygon(50% 100%, 0 0, 100% 0)'
              }} data-id="element-1442" />
                  </div>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <div className={`fixed ${hideTabBar ? 'bottom-[58px]' : 'bottom-6'} right-6 z-50 flex flex-col items-end ${className}`} data-id="element-1443">
        {/* Content Panels (Map or Rooms) - Mutually Exclusive with smooth transition */}
        <AnimatePresence mode="wait" data-id="element-1444">
          {showNavigation && <motion.div key="navigation-panel" initial={{
          opacity: 0,
          y: 10,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 10,
          scale: 0.95
        }} transition={{
          duration: 0.2
        }} className={`mb-2 w-72 rounded-xl overflow-hidden shadow-2xl backdrop-blur-xl border cursor-pointer group ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-gray-200'}`} onClick={() => setShowLargeMap(true)} data-id="element-1445">
              <div className="relative aspect-square w-full bg-slate-100" data-id="element-1446">
                <img src="https://cdn.magicpatterns.com/uploads/tUKozvi2HUbQqyNUdhAyLu/Zight_2026-02-06_at_11.09.38_AM.jpg" alt="Floorplan" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-1447" />

                {/* Expand Hint Overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100" data-id="element-1448">
                  <div className="bg-black/50 backdrop-blur-md text-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider flex items-center gap-2" data-id="element-1449">
                    <Maximize2 className="w-3 h-3" data-id="element-1450" />
                    Expand Map
                  </div>
                </div>

                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" data-id="element-1451">
                  <span className="relative flex h-4 w-4" data-id="element-1452">
                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${isDark ? 'bg-cyan-400' : 'bg-teal-500'}`} data-id="element-1453" />
                    <span className={`relative inline-flex rounded-full h-4 w-4 border-2 border-white ${isDark ? 'bg-cyan-500' : 'bg-teal-600'}`} data-id="element-1454" />
                  </span>
                  <div className={`absolute top-1/2 left-1/2 w-16 h-16 -translate-x-1/2 -translate-y-full origin-bottom bg-gradient-to-t ${isDark ? 'from-cyan-500/30' : 'from-teal-500/30'} to-transparent`} style={{
                clipPath: 'polygon(50% 100%, 0 0, 100% 0)'
              }} data-id="element-1455" />
                </div>
              </div>
            </motion.div>}

          {showRooms && <motion.div key="rooms-panel" initial={{
          opacity: 0,
          y: 10,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 10,
          scale: 0.95
        }} transition={{
          duration: 0.2
        }} className={`mb-2 w-64 rounded-xl overflow-hidden shadow-2xl backdrop-blur-xl border ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-gray-200'}`} data-id="element-1456">
              <div className={`p-4 border-b ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-100 bg-gray-50'}`} data-id="element-1457">
                <h3 className={`text-xs font-bold uppercase tracking-widest ${isDark ? 'text-white/70' : 'text-gray-500'}`} data-id="element-1458">
                  Teleport
                </h3>
              </div>
              <div className="max-h-[300px] overflow-y-auto custom-scrollbar p-2" data-id="element-1459">
                {rooms.map(room => {
              const Icon = room.icon;
              return <button key={room.id} onClick={() => onSelectRoom(room.name)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors group ${isDark ? 'text-white/70 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`} data-id="element-1460">
                      <Icon className={`w-4 h-4 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1461" />
                      <span className="text-sm font-medium" data-id="element-1462">{room.name}</span>
                    </button>;
            })}
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Tab Bar - conditionally hidden when integrated into bottom bar */}
        {!hideTabBar && <div className={`flex rounded-xl overflow-hidden shadow-xl border backdrop-blur-xl ${isDark ? 'bg-slate-900/90 border-white/10' : 'bg-white/90 border-gray-200'}`} data-id="element-1463">
            <button onClick={onToggleNavigation} className={`flex items-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${showNavigation ? isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-white/50 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-1464">
              {showNavigation ? <ChevronDown className="w-4 h-4" data-id="element-1465" /> : <ChevronUp className="w-4 h-4" data-id="element-1466" />}
              Navigation
            </button>
            <div className={`w-px ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} data-id="element-1467" />

            <button onClick={onToggleRooms} className={`flex items-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${showRooms ? isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-white/50 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-1468">
              <LayoutGrid className="w-4 h-4" data-id="element-1469" />
              Rooms
            </button>
          </div>}
      </div>
    </>;
}